import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { VenueId, VenuePhoto, VenuePhotoId } from "../../schemas/venue";
import { venueApi } from "../../venue-api";
import type { UploadProgress } from "../../venue-api/photos";
import { queryKeys } from "../query-keys";

// The venue's own mini-site photos. Every write ends by re-reading the list, so a
// refused write (cap reached in another tab, stale order) shows what is stored.

const keys = queryKeys.venuePortal;

export function useVenuePhotos(venueId: VenueId | null) {
	return useQuery({
		queryFn: venueId ? () => venueApi.photos.list(venueId) : skipToken,
		queryKey: keys.photos(venueId ?? ""),
	});
}

function usePhotoMutation<TVariables, TResult>(
	venueId: VenueId,
	mutationFn: (variables: TVariables) => Promise<TResult>,
) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn,
		onSettled: () => qc.invalidateQueries({ queryKey: keys.photos(venueId) }),
	});
}

export const useUploadVenuePhoto = (venueId: VenueId) =>
	usePhotoMutation(venueId, ({ file, onProgress }: { file: Blob; onProgress?: UploadProgress }) =>
		venueApi.photos.upload(venueId, file, onProgress),
	);

export const useUpdateVenuePhotoCaption = (venueId: VenueId) =>
	usePhotoMutation(
		venueId,
		({ photoId, caption }: { photoId: VenuePhotoId; caption: string | null }) =>
			venueApi.photos.updateCaption(venueId, photoId, caption),
	);

export const useDeleteVenuePhoto = (venueId: VenueId) =>
	usePhotoMutation(venueId, (photoId: VenuePhotoId) => venueApi.photos.remove(venueId, photoId));

/** Optimistic: the grid moves at once; the server's answer (or a re-read) settles it. */
export function useReorderVenuePhotos(venueId: VenueId) {
	const qc = useQueryClient();
	const key = keys.photos(venueId);
	return useMutation({
		mutationFn: (photoIds: readonly VenuePhotoId[]) => venueApi.photos.reorder(venueId, photoIds),
		onError: () => qc.invalidateQueries({ queryKey: key }),
		onMutate: async (photoIds) => {
			await qc.cancelQueries({ queryKey: key });
			qc.setQueryData<VenuePhoto[]>(key, (current) => current && inOrder(current, photoIds));
		},
		onSuccess: (photos) => qc.setQueryData(key, photos),
	});
}

function inOrder(photos: VenuePhoto[], ids: readonly VenuePhotoId[]): VenuePhoto[] {
	const byId = new Map(photos.map((photo) => [photo.id, photo]));
	return ids.flatMap((id) => byId.get(id) ?? []);
}
